import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPowerOff,
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faPencilAlt,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import {
  ORDERING_LOCATIONS,
  DEFAULT_PAUSE_MESSAGE,
  subscribeToOrderingStatus,
  setLocationOrderingPaused
} from '../../services/orderingStatusService';
import '../../styles/ordering-controls.css';

// Addresses shown alongside each switch so there's no doubt which warehouse
// is being paused.
const LOCATION_ADDRESSES = {
  Angola: '15 Kodak Ln, Angola, IN 46703',
  Elkhart: '56551 Mars Dr, Elkhart, IN 46516'
};

const formatUpdated = (date) => {
  if (!date) return null;
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

/**
 * One-click per-location ordering switch for the admin dashboard.
 *
 * Pausing a location stops new online bookings for the units stocked there —
 * customers can still browse and see prices, but Add to Cart and checkout are
 * blocked with the message set here. Existing orders are untouched.
 */
const OrderingControls = ({ compact = false }) => {
  const { currentUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(null); // location currently being written
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // location whose message is open
  const [draftMessage, setDraftMessage] = useState('');

  // Live subscription keeps two admins on two devices in sync.
  useEffect(() => {
    const unsubscribe = subscribeToOrderingStatus(setStatus);
    return () => unsubscribe();
  }, []);

  const updatedByLabel = () =>
    currentUser?.displayName || currentUser?.email || 'Admin';

  const handleToggle = async (location) => {
    if (saving) return;

    const isPaused = status?.[location]?.paused === true;
    setSaving(location);
    setError(null);

    try {
      await setLocationOrderingPaused(location, !isPaused, {
        message: status?.[location]?.message || DEFAULT_PAUSE_MESSAGE,
        updatedBy: updatedByLabel()
      });
      setEditing(null);
    } catch (err) {
      console.error('Error toggling ordering status:', err);
      setError(`Couldn't update ${location}. Check your connection and try again.`);
    } finally {
      setSaving(null);
    }
  };

  const startEditing = (location) => {
    setEditing(location);
    setDraftMessage(status?.[location]?.message || DEFAULT_PAUSE_MESSAGE);
  };

  const handleSaveMessage = async (location) => {
    setSaving(location);
    setError(null);

    try {
      await setLocationOrderingPaused(location, true, {
        message: draftMessage.trim() || DEFAULT_PAUSE_MESSAGE,
        updatedBy: updatedByLabel()
      });
      setEditing(null);
    } catch (err) {
      console.error('Error saving pause message:', err);
      setError(`Couldn't save the message for ${location}. Please try again.`);
    } finally {
      setSaving(null);
    }
  };

  const anyPaused = ORDERING_LOCATIONS.some((name) => status?.[name]?.paused);

  return (
    <div className={`ordering-controls ${compact ? 'compact' : ''} ${anyPaused ? 'has-paused' : ''}`}>
      <div className="ordering-controls-header">
        <h3>
          <FontAwesomeIcon icon={faPowerOff} />
          Online Ordering
        </h3>
        <p>
          Turn off a location to stop new bookings for the units stocked there.
          Customers can still browse, but can't add to cart or check out. Turn it
          back on any time — existing orders aren't affected.
        </p>
      </div>

      {error && (
        <div className="ordering-controls-error">
          <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
        </div>
      )}

      <div className="ordering-controls-grid">
        {ORDERING_LOCATIONS.map((location) => {
          const entry = status?.[location];
          const isPaused = entry?.paused === true;
          const isSaving = saving === location;
          const loading = status === null;

          return (
            <div
              key={location}
              className={`ordering-location ${isPaused ? 'paused' : 'open'}`}
            >
              <div className="ordering-location-info">
                <span className="ordering-location-name">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  {location}
                </span>
                <span className="ordering-location-address">
                  {LOCATION_ADDRESSES[location]}
                </span>
                <span className={`ordering-status-pill ${isPaused ? 'paused' : 'open'}`}>
                  <FontAwesomeIcon icon={isPaused ? faExclamationTriangle : faCheckCircle} />
                  {loading ? 'Checking…' : isPaused ? 'Ordering paused' : 'Accepting orders'}
                </span>
                {isPaused && entry?.updatedAt && (
                  <span className="ordering-meta">
                    Paused {formatUpdated(entry.updatedAt)}
                    {entry.updatedBy ? ` by ${entry.updatedBy}` : ''}
                  </span>
                )}
              </div>

              <button
                type="button"
                className={`ordering-toggle ${isPaused ? 'is-paused' : 'is-open'}`}
                onClick={() => handleToggle(location)}
                disabled={loading || isSaving}
                role="switch"
                aria-checked={!isPaused}
                aria-label={`${isPaused ? 'Resume' : 'Pause'} online ordering for ${location}`}
              >
                {isSaving ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <>
                    <span className="ordering-toggle-track">
                      <span className="ordering-toggle-thumb" />
                    </span>
                    <span className="ordering-toggle-label">
                      {isPaused ? 'Turn ordering ON' : 'Turn ordering OFF'}
                    </span>
                  </>
                )}
              </button>

              {isPaused && (
                <div className="ordering-message">
                  {editing === location ? (
                    <>
                      <label htmlFor={`pause-message-${location}`}>
                        Message shown to customers
                      </label>
                      <textarea
                        id={`pause-message-${location}`}
                        rows={3}
                        value={draftMessage}
                        onChange={(e) => setDraftMessage(e.target.value)}
                        maxLength={300}
                      />
                      <div className="ordering-message-actions">
                        <button
                          type="button"
                          className="primary-button"
                          onClick={() => handleSaveMessage(location)}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving…' : 'Save message'}
                        </button>
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => setEditing(null)}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="ordering-message-preview">
                        “{entry?.message || DEFAULT_PAUSE_MESSAGE}”
                      </p>
                      <button
                        type="button"
                        className="ordering-edit-link"
                        onClick={() => startEditing(location)}
                      >
                        <FontAwesomeIcon icon={faPencilAlt} /> Edit message
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderingControls;
